/**
 * Auth gate for Self-Scan entry (AC-02 / residual G1 / G9).
 * Parks same-tenant Self-Scan return target via saveReturnTarget (not platform hub remap).
 * Prefer scanner deep-link when SP known; else buildSelfScanPath (?salesPointId / ?basketPublicId).
 */

import type { ReactNode } from 'react';
import { useLayoutEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { evaluateIdentityPipeline } from '../../../navigation/identityPipeline.js';
import { useCustomerSession } from '../../../shared/session/CustomerSessionProvider.js';
import { useOidcLandGuardPending } from '../../../shared/session/useOidcLandGuardPending.js';
import {
  isSessionEndingLatchActive,
} from '../../../shared/session/customerAuthSync.js';
import { useTenantCode } from '../../../shared/tenant/TenantProvider.js';
import {
  savePlatformReturnTarget,
  saveReturnTarget,
} from '../../../shared/session/returnTarget.js';
import {
  buildSelfScanPath,
  buildShopScannerSelfScanPath,
} from '../selfScanPaths.js';
import {
  resolveSelfScanBasketPublicId,
  resolveSelfScanSalesPointId,
} from '../isSelfScanMode.js';

interface RequireSelfScanAuthProps {
  readonly children: ReactNode;
}

export function RequireSelfScanAuth({
  children,
}: RequireSelfScanAuthProps): JSX.Element | null {
  const { t } = useTranslation('customer');
  const tenantCode = useTenantCode();
  const { session, sessionHydrated } = useCustomerSession();
  const location = useLocation();
  const { oidcLandingPending, oidcLandBudgetExhausted } =
    useOidcLandGuardPending();
  const windowOidcSearch =
    typeof window !== 'undefined' ? window.location.search : '';

  const pipelineDecision = useMemo(() => {
    return evaluateIdentityPipeline({
      routeMatched: true,
      entitlementsLoading: false,
      entitlementsGranted: true,
      entitlementMode: 'inline-unavailable',
      sessionHydrated,
      sessionPresent: session !== null,
      accountMeReady: true,
      accountMeValid: true,
      oidcLandingPending,
      oidcLandBudgetExhausted,
    });
  }, [
    oidcLandingPending,
    oidcLandBudgetExhausted,
    session,
    sessionHydrated,
  ]);

  useLayoutEffect(() => {
    if (pipelineDecision.redirectToSignIn !== true) {
      return;
    }
    if (isSessionEndingLatchActive()) {
      return;
    }
    const search = windowOidcSearch || location.search;
    const salesPointId = resolveSelfScanSalesPointId(search);
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const basketPublicId = resolveSelfScanBasketPublicId(
      search,
      location.pathname.includes('/self-scan/')
        ? pathSegments[pathSegments.length - 1] ?? null
        : null,
    );

    // Tenant-scoped park (same tenant + SP Self-Scan context).
    // Prefer scanner deep-link with kioskId when SP known (platform allowlist).
    // Else buildSelfScanPath emits /{tenant}/self-scan?salesPointId&basketPublicId.
    const scannerPark =
      salesPointId !== null
        ? `${buildShopScannerSelfScanPath(tenantCode, { salesPointId })}${
            basketPublicId !== null
              ? `&basketPublicId=${encodeURIComponent(basketPublicId)}`
              : ''
          }`
        : buildSelfScanPath(tenantCode, {
            salesPointId,
            basketPublicId,
          });
    saveReturnTarget(scannerPark, { tenantCode, source: 'shop' });
    savePlatformReturnTarget(scannerPark);
  }, [
    pipelineDecision.redirectToSignIn,
    tenantCode,
    location.pathname,
    location.search,
    windowOidcSearch,
  ]);

  if (
    !pipelineDecision.allowScreenInit &&
    pipelineDecision.redirectToSignIn !== true
  ) {
    return (
      <p className="p-4 text-sm text-[var(--color-on-surface-muted)]" role="status">
        {t('account.sessionHydrating')}
      </p>
    );
  }

  if (
    pipelineDecision.allowScreenInit &&
    pipelineDecision.redirectToSignIn !== true
  ) {
    return <>{children}</>;
  }

  if (isSessionEndingLatchActive()) {
    return (
      <p className="p-4 text-sm text-[var(--color-on-surface-muted)]" role="status">
        {t('account.sessionHydrating')}
      </p>
    );
  }

  if (location.pathname === '/sign-in') {
    return null;
  }

  return <Navigate to="/sign-in" replace />;
}
